import {
  formatCorpusPathSegment,
  parseCorpusPathSegment,
  parseGameSourcesFromUrl,
  type GameSource,
} from '../../common/gameSource';
import { getOpeningFamilyName } from './openingLabel';
import {
  openingFamilySlug,
  openingVariationSlug,
  playerBrowseSlug,
} from './openingBrowseSlugs';

export {
  openingFamilySlug,
  openingFamilySlugToSearchQueries,
  openingVariationSlug,
  playerBrowseSlug,
} from './openingBrowseSlugs';

export const GAMES_BROWSE_PATH = '/games/browse';
export const GAMES_BROWSE_OPENINGS_PATH = `${GAMES_BROWSE_PATH}/openings`;

export type GamesBrowsePathMatch =
  | { kind: 'hub' }
  | { kind: 'openingFamily'; familySlug: string }
  | {
      kind: 'openingVariation';
      familySlug: string;
      variationSlug: string;
      eco?: string;
    }
  | { kind: 'player'; corpus: string; playerSlug: string }
  | {
      kind: 'playerOpening';
      corpus: string;
      playerSlug: string;
      familySlug: string;
      variationSlug?: string;
      eco?: string;
    };

type OpeningSlugFields = {
  opening: string;
  eco?: string;
};

/** True when the opening name includes a variation after a colon. */
export const openingNameHasVariation = (opening: string): boolean =>
  opening.trim().includes(':');

export const gamesBrowseOpeningPath = (
  familySlug: string,
  variationSlug?: string,
  eco?: string,
): string => {
  const family = familySlug.trim();
  if (!family) {
    return GAMES_BROWSE_OPENINGS_PATH;
  }
  const base = `${GAMES_BROWSE_OPENINGS_PATH}/${encodeURIComponent(family)}`;
  const variation = variationSlug?.trim();
  if (!variation) {
    return base;
  }
  const variationPath = `${base}/${encodeURIComponent(variation)}`;
  const ecoValue = eco?.trim().toLowerCase();
  if (!ecoValue) {
    return variationPath;
  }
  return `${variationPath}/${encodeURIComponent(ecoValue)}`;
};

export const gamesBrowsePlayerPath = (
  corpus: string,
  playerSlug: string,
): string =>
  `${GAMES_BROWSE_PATH}/${encodeURIComponent(corpus.trim())}/${encodeURIComponent(playerSlug.trim())}`;

export const gamesBrowsePlayerOpeningPath = (
  corpus: string,
  playerSlug: string,
  opening: string,
  eco?: string,
): string => {
  const familySlug = openingFamilySlug(opening);
  const base = `${gamesBrowsePlayerPath(corpus, playerSlug)}/openings/${encodeURIComponent(familySlug)}`;
  if (!openingNameHasVariation(opening)) {
    return base;
  }
  const variationPath = `${base}/${encodeURIComponent(openingVariationSlug(opening))}`;
  const ecoValue = eco?.trim().toLowerCase();
  if (!ecoValue) {
    return variationPath;
  }
  return `${variationPath}/${encodeURIComponent(ecoValue)}`;
};

/** Whether sibling openings share the same family/variation slugs (needs ECO in URL). */
export const playerOpeningPathNeedsEco = (
  openings: OpeningSlugFields[],
  opening: OpeningSlugFields,
): boolean => {
  const family = openingFamilySlug(opening.opening);
  const hasVariation = openingNameHasVariation(opening.opening);
  const variation = hasVariation
    ? openingVariationSlug(opening.opening)
    : undefined;
  const siblings = openings.filter((row) => {
    if (openingFamilySlug(row.opening) !== family) {
      return false;
    }
    if (hasVariation) {
      return (
        openingNameHasVariation(row.opening) &&
        openingVariationSlug(row.opening) === variation
      );
    }
    return !openingNameHasVariation(row.opening);
  });
  return siblings.length > 1;
};

export const matchPlayerOpeningFromSlugs = <T extends OpeningSlugFields>(
  openings: T[],
  familySlug: string,
  variationSlug?: string,
  eco?: string,
): T | null => {
  const ecoValue = eco?.trim().toLowerCase();
  const candidates = openings.filter((row) => {
    if (openingFamilySlug(row.opening) !== familySlug) {
      return false;
    }
    if (!variationSlug) {
      return !openingNameHasVariation(row.opening);
    }
    if (
      !openingNameHasVariation(row.opening) ||
      openingVariationSlug(row.opening) !== variationSlug
    ) {
      return false;
    }
    if (ecoValue && row.eco?.trim().toLowerCase() !== ecoValue) {
      return false;
    }
    return true;
  });
  return candidates[0] ?? null;
};

export const parseGamesBrowsePath = (
  pathname: string,
): GamesBrowsePathMatch | null => {
  if (pathname === GAMES_BROWSE_PATH) {
    return { kind: 'hub' };
  }

  const openingsPrefix = `${GAMES_BROWSE_OPENINGS_PATH}/`;
  if (pathname.startsWith(openingsPrefix)) {
    const segments = pathname
      .slice(openingsPrefix.length)
      .split('/')
      .filter(Boolean)
      .map((segment) => decodeURIComponent(segment));
    if (segments.length === 1) {
      return { kind: 'openingFamily', familySlug: segments[0] };
    }
    if (segments.length === 2) {
      return {
        kind: 'openingVariation',
        familySlug: segments[0],
        variationSlug: segments[1],
      };
    }
    if (segments.length === 3) {
      return {
        kind: 'openingVariation',
        familySlug: segments[0],
        variationSlug: segments[1],
        eco: segments[2],
      };
    }
    return null;
  }

  const browsePrefix = `${GAMES_BROWSE_PATH}/`;
  if (pathname.startsWith(browsePrefix)) {
    const segments = pathname
      .slice(browsePrefix.length)
      .split('/')
      .filter(Boolean)
      .map((segment) => decodeURIComponent(segment));
    if (
      segments.length >= 4 &&
      segments[0] !== 'openings' &&
      segments[0] !== 'mine' &&
      segments[2] === 'openings'
    ) {
      if (segments.length === 4) {
        return {
          kind: 'playerOpening',
          corpus: segments[0],
          playerSlug: segments[1],
          familySlug: segments[3],
        };
      }
      if (segments.length === 5) {
        return {
          kind: 'playerOpening',
          corpus: segments[0],
          playerSlug: segments[1],
          familySlug: segments[3],
          variationSlug: segments[4],
        };
      }
      if (segments.length === 6) {
        return {
          kind: 'playerOpening',
          corpus: segments[0],
          playerSlug: segments[1],
          familySlug: segments[3],
          variationSlug: segments[4],
          eco: segments[5],
        };
      }
      return null;
    }
    if (
      segments.length === 2 &&
      segments[0] !== 'openings' &&
      segments[0] !== 'mine'
    ) {
      return {
        kind: 'player',
        corpus: segments[0],
        playerSlug: segments[1],
      };
    }
  }

  return null;
};

export const isLegacyGamesBrowseQueryUrl = (
  pathname: string,
  search: string,
): boolean =>
  pathname === GAMES_BROWSE_PATH && hasBrowseParamsInSearch(search);

export const GAMES_HUB_PATH = '/games';
export const MY_GAMES_PATH = '/games/mine';

export const myGamesPath = (options?: {
  import?: boolean;
  page?: number;
}): string => {
  const params = new URLSearchParams();
  if (options?.import) {
    params.set('import', '1');
  }
  if (options?.page != null && options.page > 1) {
    params.set('page', String(options.page));
  }
  const query = params.toString();
  return `${MY_GAMES_PATH}${query ? `?${query}` : ''}`;
};

export const parseMyGamesSearch = (search: string): { page: number } => {
  const params = new URLSearchParams(search);
  const pageParam = params.get('page');
  const parsedPage =
    pageParam != null ? Number.parseInt(pageParam, 10) : Number.NaN;
  const page =
    Number.isFinite(parsedPage) && parsedPage >= 1 ? parsedPage : 1;
  return { page };
};

export const gamesBrowsePath = (options?: {
  opening?: string;
  variation?: string;
  eco?: string;
  player?: string;
  sources?: GameSource[];
}): string => {
  const player = options?.player?.trim();
  const opening = options?.opening?.trim();
  if (player && opening) {
    const source = options?.sources?.[0] ?? 'twic';
    return gamesBrowsePlayerOpeningPath(
      formatCorpusPathSegment(source),
      playerBrowseSlug(player),
      opening,
      options?.eco,
    );
  }

  if (player) {
    const source = options?.sources?.[0] ?? 'twic';
    return gamesBrowsePlayerPath(
      formatCorpusPathSegment(source),
      playerBrowseSlug(player),
    );
  }

  if (opening) {
    const family = getOpeningFamilyName(opening);
    const familySlug = openingFamilySlug(family);
    const variation = options?.variation?.trim();
    if (variation) {
      return gamesBrowseOpeningPath(
        familySlug,
        openingVariationSlug(variation),
        options?.eco,
      );
    }
    return gamesBrowseOpeningPath(familySlug);
  }

  return GAMES_BROWSE_PATH;
};

export const myGamePath = (gameId: string): string =>
  `${MY_GAMES_PATH}/${encodeURIComponent(gameId)}`;

export const hasBrowseParamsInSearch = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return Boolean(params.get('opening')?.trim() || params.get('player')?.trim());
};

const normalizeLegacyBrowseSearch = (search: string): string => {
  const params = new URLSearchParams(search);
  if (params.has('source') && !params.has('sources')) {
    const legacy = params.get('source')?.trim().toLowerCase();
    params.delete('source');
    if (legacy === 'lichess' || legacy === 'lichess-elite') {
      params.set('sources', 'lichess-elite');
    } else if (legacy === 'master' || legacy === 'twic') {
      params.set('sources', 'master');
    }
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const legacyGamesBrowseQueryToPath = (search: string): string | null => {
  const normalizedSearch = normalizeLegacyBrowseSearch(search);
  if (!hasBrowseParamsInSearch(normalizedSearch)) {
    return null;
  }

  const { opening, player, sources } = parseGamesBrowseSearch(normalizedSearch);
  if (opening) {
    return gamesBrowsePath({ opening: getOpeningFamilyName(opening) });
  }
  if (player) {
    return gamesBrowsePath({
      player,
      sources: [sources[0] ?? 'twic'],
    });
  }
  return null;
};

export const legacyGamesBrowseRedirect = (search: string): string | null => {
  const path = legacyGamesBrowseQueryToPath(search);
  if (path) {
    return path;
  }
  if (!hasBrowseParamsInSearch(search)) {
    return null;
  }
  return GAMES_BROWSE_PATH;
};

export const hasExplicitSourcesInGamesBrowseSearch = (
  search: string,
): boolean => new URLSearchParams(search).has('sources');

export const parseGamesBrowseSearch = (
  search: string,
): {
  opening: string;
  player: string;
  sources: GameSource[];
} => {
  const params = new URLSearchParams(search);
  const opening = params.get('opening')?.trim() ?? '';
  const player = params.get('player')?.trim() ?? '';
  const sources = parseGameSourcesFromUrl(params.get('sources'));

  return { opening, player, sources };
};

export const stripLegacyGamesBrowseSourceParam = (search: string): string => {
  const params = new URLSearchParams(search);
  if (!params.has('source')) {
    return search;
  }
  params.delete('source');
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const hasLegacyGamesBrowseSourceParam = (search: string): boolean =>
  new URLSearchParams(search).has('source');

export const isCanonicalGamesBrowseUrl = (
  pathname: string,
  search: string,
  options: {
    opening?: string;
    variation?: string;
    eco?: string;
    player?: string;
    sources?: GameSource[];
  },
): boolean =>
  pathname === gamesBrowsePath(options) &&
  !hasBrowseParamsInSearch(search) &&
  !hasLegacyGamesBrowseSourceParam(search);

export const resolvePlayerSourceFromBrowsePath = (
  corpus: string,
): GameSource | null => parseCorpusPathSegment(corpus);

export const isGamesBrowsePath = (pathname: string): boolean =>
  pathname === GAMES_BROWSE_PATH;

export const isGamesHubPath = (pathname: string): boolean =>
  pathname === GAMES_HUB_PATH;

export const isMyGamesPath = (pathname: string): boolean =>
  pathname === MY_GAMES_PATH || pathname.startsWith(`${MY_GAMES_PATH}/`);
